import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  linkWithCredential,
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  AuthError,
  User,
  UserCredential,
  reauthenticateWithCredential,
  AuthCredential
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { BetaAccessService } from './betaAccessService';

export interface AuthResult {
  success: boolean;
  user?: User;
  message?: string;
  accountLinked?: boolean;
  requiresLinking?: boolean;
  existingProvider?: string;
}

export class AuthService {
  private static googleProvider = new GoogleAuthProvider();

  /**
   * Sign in with Google - includes beta access check and account linking
   */
  static async signInWithGoogle(): Promise<AuthResult> {
    try {
      // Sign in with Google popup
      const result: UserCredential = await signInWithPopup(auth, this.googleProvider);
      const user = result.user;
      const email = user.email;

      if (!email) {
        await auth.signOut();
        return {
          success: false,
          message: 'No email associated with this Google account'
        };
      }

      // Check beta access
      const isBetaUser = await BetaAccessService.isEmailAllowed(email);
      if (!isBetaUser) {
        await auth.signOut();
        return {
          success: false,
          message: 'This app is currently in beta. Your email is not on the access list. Please contact the administrator for access.'
        };
      }

      // Check if this email already has an account with different provider
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      
      if (signInMethods.length > 0 && !signInMethods.includes('google.com')) {
        // Email exists with different provider (likely password)
        // In this case, the Google sign-in has already succeeded,
        // but we should note that linking might be beneficial
        return {
          success: true,
          user: user,
          message: 'Signed in with Google. You also have an email/password account that can be linked.',
          requiresLinking: true,
          existingProvider: signInMethods[0]
        };
      }

      // Successful sign-in
      return {
        success: true,
        user: user,
        message: 'Successfully signed in with Google'
      };

    } catch (error) {
      const authError = error as AuthError;
      console.error('Google sign-in error:', authError);

      // Handle specific error cases
      if (authError.code === 'auth/popup-closed-by-user') {
        return {
          success: false,
          message: 'Sign-in cancelled'
        };
      }

      if (authError.code === 'auth/account-exists-with-different-credential') {
        // This happens when the email is already used with a different provider
        return {
          success: false,
          message: 'An account already exists with this email. Please sign in with your original method first, then link your Google account.',
          requiresLinking: true
        };
      }

      return {
        success: false,
        message: authError.message || 'Failed to sign in with Google'
      };
    }
  }

  /**
   * Link Google account to existing email/password account
   */
  static async linkGoogleAccount(password: string): Promise<AuthResult> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        return {
          success: false,
          message: 'No user currently signed in'
        };
      }

      // Re-authenticate with email/password
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);

      // Sign in with Google popup to get credential
      const googleResult = await signInWithPopup(auth, this.googleProvider);
      
      // Check if the Google email matches the current user's email
      if (googleResult.user.email !== currentUser.email) {
        return {
          success: false,
          message: 'The Google account email must match your current email'
        };
      }

      // Get the credential from the provider
      const googleCredential = GoogleAuthProvider.credentialFromResult(googleResult);
      if (!googleCredential) {
        return {
          success: false,
          message: 'Failed to get Google credentials'
        };
      }

      // Link the accounts
      await linkWithCredential(currentUser, googleCredential);

      return {
        success: true,
        user: currentUser,
        message: 'Successfully linked Google account',
        accountLinked: true
      };

    } catch (error) {
      const authError = error as AuthError;
      console.error('Account linking error:', authError);

      if (authError.code === 'auth/provider-already-linked') {
        return {
          success: false,
          message: 'Google account is already linked to this account'
        };
      }

      if (authError.code === 'auth/credential-already-in-use') {
        return {
          success: false,
          message: 'This Google account is already linked to another user'
        };
      }

      return {
        success: false,
        message: authError.message || 'Failed to link Google account'
      };
    }
  }

  /**
   * Check if current user has Google provider linked
   */
  static hasGoogleProvider(): boolean {
    const user = auth.currentUser;
    if (!user) return false;

    return user.providerData.some(provider => provider.providerId === 'google.com');
  }

  /**
   * Get list of auth providers for current user
   */
  static getAuthProviders(): string[] {
    const user = auth.currentUser;
    if (!user) return [];

    return user.providerData.map(provider => provider.providerId);
  }
}